import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, merge, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WSService } from '../_services/ws.service';
import { ConnectionStatusService } from '../_services/connection-status.service';
import { checkInternet, InternetState, isLocalHostname } from '../_helpers/internet-check';

/** Kratak prekid (reconnect, prebacivanje servera) ne sme da trepne porukom. */
const SHOW_DELAY_MS = 1500;
/** Javne servere (provera interneta) zovemo najviše jednom u ovom periodu dok server ne odgovara. */
const INTERNET_RECHECK_MS = 30000;
/** Razlozi su isti kao kod ručne prijave kodom — deli se `codeSignin.errors.*` grupa prevoda. */
const OFFLINE_KEY = 'codeSignin.errors.offline';

/**
 * Status veze (login ekran, ili traka u aplikaciji): vidi se samo kad server nije dostupan — razlog +
 * „pokušavamo da se povežemo", a nestaje sam kad se veza vrati (i prijava, ako je sačuvana, nastavi sama).
 * Signal: navigator.onLine (uređaj van mreže — odmah) i WS veza, koja se otvara i bez prijave:
 * nije povezan uz bar jedan neuspeo pokušaj = server nije dostupan. App-ovi bez WS-a
 * (environment.disableWebsocket) nikad ne broje neuspehe, pa im status ne prijavljuje lažno.
 */
@Component({
  selector: 'app-connection-status',
  templateUrl: './connection-status.component.html',
  styleUrls: ['./connection-status.component.scss'],
  standalone: false
})
export class ConnectionStatusComponent implements OnInit, OnDestroy {
  readonly offlineKey = OFFLINE_KEY;

  /** 'card' = login (iznad prijave i PIN-a), 'bar' = traka u aplikaciji. Obe zamenjuju offline
   *  modal dok su na ekranu (jedna poruka, ne dve); na loginu i okvir koda ne ponavlja razlog. */
  @Input() variant: 'card' | 'bar' = 'card';

  /** Zadrška pre prikaza, i za „van mreže" (u aplikaciji kratki prekidi pri povratku iz pozadine
   *  ili roaming-u ne smeju da trepnu trakom). Bez nje: van mreže odmah, server posle SHOW_DELAY_MS. */
  @Input() delayMs?: number;

  /** Razlog za prikaz (i18n ključ + parametri); undefined = veza je u redu, ništa se ne prikazuje. */
  reason?: { key: string; params?: object };

  private destroy = new Subject<void>();
  private showTimer?: number;
  private internet?: InternetState;
  private internetCheckedAt = 0;
  private internetChecking = false;

  constructor(private ws: WSService,
    private status: ConnectionStatusService) {
  }

  ngOnInit(): void {
    this.status.mount();

    merge(
      fromEvent(window, 'online'),
      fromEvent(window, 'offline'),
      fromEvent(document, 'visibilitychange'),
      this.ws.connected,
      this.ws.connectRetryCount
    ).pipe(
      takeUntil(this.destroy)
    ).subscribe(() => this.evaluate());
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.cancelShow();
    this.setReason(undefined);
    this.status.unmount();
  }

  private get serverFailing(): boolean {
    return !this.ws.connected.value && this.ws.connectRetryCount.value > 0;
  }

  private evaluate() {
    if (document.hidden) {
      // U pozadini (telefon zaključan / druga aplikacija) WS pada i zadrška bi istekla tamo —
      // pri otvaranju bi traka već stajala dok se veza ne vrati. Zato se u pozadini ništa ne
      // broji ni prikazuje; po povratku kreće puna zadrška i nova provera interneta.
      this.cancelShow();
      this.setReason(undefined);
      this.internet = undefined;
      return;
    }

    const offline = navigator.onLine === false;
    if (offline) {
      // internet se posle povratka mreže proverava iznova
      this.internet = undefined;
    } else if (!this.serverFailing) {
      this.cancelShow();
      this.setReason(undefined);
      return;
    }

    if (!offline && this.reason?.key === OFFLINE_KEY) {
      // mreža se upravo vratila, a WS još nije probao iznova — ne tvrdi odmah da server ne radi
      this.setReason(undefined);
    }

    if (this.reason) {
      // već se prikazuje — samo osveži razlog
      this.applyReason();
      return;
    }

    const delay = this.delayMs ?? (offline ? 0 : SHOW_DELAY_MS);
    if (delay <= 0) {
      this.applyReason();
    } else if (this.showTimer === undefined) {
      this.showTimer = window.setTimeout(() => {
        this.showTimer = undefined;
        // za vreme čekanja se veza možda vratila
        if (!document.hidden && (navigator.onLine === false || this.serverFailing)) {
          this.applyReason();
        }
      }, delay);
    }
  }

  /** Razlog za trenutno stanje: uređaj van mreže, ili server ne odgovara. */
  private applyReason() {
    if (navigator.onLine === false) {
      this.setReason({ key: OFFLINE_KEY });
    } else {
      this.updateUnreachableReason();
    }
  }

  /** Server ne odgovara — zašto: lokalni server, ili (provera interneta) nema interneta / DNS / sam server. */
  private updateUnreachableReason() {
    const host = this.backendHost;
    if (this.backendIsLocal) {
      this.setReason({ key: 'codeSignin.errors.localServer', params: { host } });
      return;
    }

    this.refreshInternetState();
    switch (this.internet) {
      case 'down': this.setReason({ key: 'codeSignin.errors.noInternet' }); return;
      case 'dns': this.setReason({ key: 'codeSignin.errors.dns' }); return;
      case 'ok': this.setReason({ key: 'codeSignin.errors.serverDown', params: { host } }); return;
    }
    // provera interneta još traje
    this.setReason({ key: 'codeSignin.errors.unreachable', params: { host } });
  }

  private refreshInternetState() {
    if (this.internetChecking ||
      (this.internet && Date.now() - this.internetCheckedAt < INTERNET_RECHECK_MS)) {
      return;
    }
    this.internetChecking = true;
    checkInternet().then(state => {
      this.internetChecking = false;
      this.internet = state;
      this.internetCheckedAt = Date.now();
      // rezultat stiže posle prikaza — osveži razlog ako problem i dalje traje
      if (this.reason && navigator.onLine !== false && this.serverFailing) {
        this.updateUnreachableReason();
      }
    });
  }

  private cancelShow() {
    window.clearTimeout(this.showTimer);
    this.showTimer = undefined;
  }

  private setReason(reason?: { key: string; params?: object }) {
    this.reason = reason;
    if (this.status.showing$.value !== !!reason) {
      this.status.showing$.next(!!reason);
    }
  }

  private get backendHost(): string {
    try {
      return new URL(environment.apiBase, location.href).host;
    } catch {
      return location.host;
    }
  }

  private get backendIsLocal(): boolean {
    try {
      return isLocalHostname(new URL(environment.apiBase, location.href).hostname);
    } catch {
      return false;
    }
  }
}
